/**
 * Opens the borrower app with the given tokens, either via ReactNativeWebView or window.open
 * @param token - Auth token
 * @param refreshToken - Refresh token
 */
export function openUrlApp(url: string) {
  if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined') {
    const eventData = {
      event: 'openURL',
      url,
    }
    window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
  } else {
    window.open(url, '_blank')
  }
}
/* eslint-disable @typescript-eslint/ban-ts-comment */
export const parseFloatComma = (floatStr?: string | number): number => {
  if (floatStr === undefined || floatStr === null) {
    return NaN
  }

  if (typeof floatStr === 'number') {
    return floatStr
  }

  return parseFloat(floatStr.replace(',', '.'))
}

export function selectFirstNotUndefinedField<T>(...params: Array<T | null>): T | null {
  return params.find((value) => value !== undefined && value !== null) || null
}

export function isValidHttpUrl(s: string) {
  let url: URL

  try {
    url = new URL(s)
  } catch (err) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

export function capitalizeSentence(s: string) {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function uncapitalizeSentence(s: string) {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toLowerCase() + s.slice(1)
}

export function capitalizeWords(s: string) {
  if (typeof s !== 'string') return ''
  const arr = s.split(' ')

  for (let i = 0; i < arr.length; i += 1) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1)
  }

  return arr.join(' ')
}

export const arrayBufferToBase64 = (param: string | ArrayBuffer) => {
  if (typeof param === 'string') {
    return param
  }
  let binary = ''
  const bytes = new Uint8Array(param)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

export const readFileToBase64Async = (file: File): Promise<string | ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (e) => resolve(e.target?.result!)
    reader.onerror = (error) => reject(error)
  })

export const urlToBase64Async = async (url: string): Promise<string> => {
  const response = await fetch(url)
  const blob = await response.blob()
  const base64 = await readFileToBase64Async(blob as File)
  return typeof base64 === 'string' ? base64 : arrayBufferToBase64(base64)
}

export const convertToAPIReadyBase64Async = async (
  input: File | string | ArrayBuffer,
  keepPrefix = true,
): Promise<string> => {
  let base64final: string

  if (typeof input === 'string') {
    const isAbsoluteHttp = isValidHttpUrl(input)
    const isRelativePath = input.startsWith('/')

    if (isAbsoluteHttp || isRelativePath) {
      const url = isAbsoluteHttp ? input : new URL(input, window.location.origin).toString()
      base64final = await urlToBase64Async(url)
    } else {
      base64final = input // already base64 or plain string
    }
  } else {
    let tmp: File | string | ArrayBuffer = input
    if (tmp instanceof File) tmp = await readFileToBase64Async(tmp)
    base64final = arrayBufferToBase64(tmp)
  }

  if (!keepPrefix) {
    const parts = base64final.split(',')
    base64final = parts.length > 1 ? parts[1] : parts[0]
  }

  return base64final
}

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
    ? RecursivePartial<T[P]>
    : T[P]
}

export type KeyOf<T> = string & keyof T
export type ValueOf<T> = T[KeyOf<T>]

export type Keys<T> = KeyOf<T>[]

/**
 * Same as `Object.keys(obj)` but with type inference
 * @param obj
 */
export const ObjectKeys = <T extends object>(obj: T): Keys<T> => {
  return Object.keys(obj) as Keys<T>
}

export type Values<T> = ValueOf<T>[]

/**
 * Same as `Object.values(obj)` but with type inference
 * @param obj
 */
export const ObjectValues = <T extends object>(obj: T): Values<T> => {
  return Object.values(obj) as Values<T>
}

export type Entries<T> = [KeyOf<T>, ValueOf<T>][]

/**
 * Same as `Object.entries(obj)` but with type inference
 * @param obj
 */
export const ObjectEntries = <T extends object>(obj: T): Entries<T> => {
  return Object.entries(obj) as Entries<T>
}

const MAX_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_PIXELS = 20_000_000

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']

// fallback si certains navigateurs mettent jpg en image/jpeg uniquement
const isValidType = (file: File) => ALLOWED_MIME_TYPES.includes(file.type)

const validateImageResolution = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (file.type === 'application/pdf') return resolve(true)

    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      const pixels = img.width * img.height
      URL.revokeObjectURL(url)
      resolve(pixels <= MAX_PIXELS)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(false)
    }

    img.src = url
  })
}

export const validateIDFile = async (file: File): Promise<string | null> => {
  if (!file) return 'File is required'

  if (!isValidType(file)) {
    return 'Accepted formats: PNG, JPEG, JPG, PDF'
  }

  if (file.size > MAX_SIZE) {
    return 'Max size: 10 MB'
  }

  const isValidResolution = await validateImageResolution(file)

  if (!isValidResolution) {
    return 'Max resolution: 20 mpx'
  }

  return null
}
